// utilities/mtnStatementImport.js
// ---------------------------------------------------------------------------
// Pure helpers for turning an MTN MoMo disbursement statement (CSV / XLSX,
// already parsed into header + row arrays by `xlsx`) into the row shape the
// wallet's POST /api/disbursement/admin/reports/mtn-statement/reconcile
// expects:
//   { financialTransactionId?, externalId?, referenceId?, amount, status?, date?, phone? }
// No DOM, no xlsx import — the component does the file reading.
// ---------------------------------------------------------------------------

export const MTN_FIELDS = [
  "financialTransactionId",
  "externalId",
  "referenceId",
  "amount",
  "status",
  "date",
  "phone",
];

export const MTN_FIELD_LABELS = {
  financialTransactionId: "Financial transaction id",
  externalId:             "External id (our disbursementId)",
  referenceId:            "Reference id (X-Reference-Id)",
  amount:                 "Amount",
  status:                 "Status",
  date:                   "Date",
  phone:                  "Phone / MSISDN",
};

// Precedence matters: a header is claimed by the FIRST field whose pattern
// matches it, and each field takes only its first matching header. So
// "Financial Transaction Id" lands on financialTransactionId, not on a later
// generic pattern, and "Reference Number" on referenceId rather than phone.
const HEURISTICS = [
  ["financialTransactionId", /financial.*id|fin(ancial)?\s*tx|transaction\s*id/i],
  ["externalId",             /external.*(id|ref)/i],
  ["referenceId",            /reference/i],
  ["amount",                 /amount/i],
  ["status",                 /status/i],
  ["date",                   /date|time/i],
  ["phone",                  /(msisdn|phone|number)/i],
];

const cleanHeader = (h) => (h == null ? "" : String(h)).replace(/\s+/g, " ").trim();

/**
 * Map statement headers onto the contract fields.
 * @param {string[]} headers  raw header cells, in column order
 * @returns {{[field:string]: string|null}} field → header text (null = unmapped)
 */
export function mapMtnStatementColumns(headers) {
  const list = Array.isArray(headers) ? headers.map(cleanHeader) : [];
  const claimed = new Set();
  const mapping = {};
  MTN_FIELDS.forEach((f) => { mapping[f] = null; });
  HEURISTICS.forEach(([field, re]) => {
    const hit = list.find((h) => h && !claimed.has(h) && re.test(h));
    if (hit) {
      mapping[field] = hit;
      claimed.add(hit);
    }
  });
  return mapping;
}

/**
 * "UGX 6,000" → 6000, "6000.00" → 6000, "6,000.50" → 6000.5, "(1,200)" → -1200,
 * numbers pass through, anything non-numeric → null.
 */
export function parseAmount(v) {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  let s = String(v).trim();
  if (!s) return null;
  const negative = /^\(.*\)$/.test(s) || /^-/.test(s) || /-$/.test(s);
  s = s.replace(/[^0-9.]/g, "");
  if (!s || s === ".") return null;
  // Tolerate a thousands "." when there is more than one dot (e.g. "6.000.00")
  const parts = s.split(".");
  if (parts.length > 2) s = parts.slice(0, -1).join("") + "." + parts[parts.length - 1];
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);

/** Date cell → ISO string when parsable, else the trimmed raw text (or null). */
export function normaliseDate(v) {
  if (v == null || v === "") return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.toISOString();
  if (typeof v === "number") {
    // Excel serial (days since 1899-12-30) when it is in a sane range
    if (v > 20000 && v < 80000) return new Date(EXCEL_EPOCH_MS + Math.round(v * 86400000)).toISOString();
    if (v > 1e11) return new Date(v).toISOString(); // epoch ms
    return String(v);
  }
  const s = String(v).trim();
  if (!s) return null;
  // dd/mm/yyyy [hh:mm[:ss]] FIRST — MTN portal exports are day-first and
  // Date.parse would silently read "04/09/2026" as April 9th.
  const m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (m) {
    const d = new Date(Date.UTC(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)));
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  const t = Date.parse(s);
  if (Number.isFinite(t)) return new Date(t).toISOString();
  return s;
}

const cleanText = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
};

const cleanPhone = (v) => {
  const s = cleanText(v);
  if (!s) return null;
  const digits = s.replace(/[^\d]/g, "");
  return digits || s;
};

/**
 * Turn parsed sheet rows (objects keyed by header text) into contract rows
 * using a mapping from mapMtnStatementColumns() (possibly user-overridden).
 * Rows with neither an amount nor any identifier are dropped.
 */
export function normaliseMtnRows(rows, mapping) {
  if (!Array.isArray(rows) || !mapping) return [];
  const pick = (row, field) => {
    const h = mapping[field];
    if (!h) return undefined;
    if (row[h] !== undefined) return row[h];
    // tolerate header whitespace differences between mapping and row keys
    const key = Object.keys(row).find((k) => cleanHeader(k) === cleanHeader(h));
    return key ? row[key] : undefined;
  };
  const out = [];
  rows.forEach((row) => {
    if (!row || typeof row !== "object") return;
    const financialTransactionId = cleanText(pick(row, "financialTransactionId"));
    const externalId  = cleanText(pick(row, "externalId"));
    const referenceId = cleanText(pick(row, "referenceId"));
    const amount = parseAmount(pick(row, "amount"));
    const status = cleanText(pick(row, "status"));
    const date   = normaliseDate(pick(row, "date"));
    const phone  = cleanPhone(pick(row, "phone"));
    if (amount == null && !financialTransactionId && !externalId && !referenceId) return;
    const r = { amount: amount == null ? null : amount };
    if (financialTransactionId) r.financialTransactionId = financialTransactionId;
    if (externalId)  r.externalId  = externalId;
    if (referenceId) r.referenceId = referenceId;
    if (status) r.status = status.toUpperCase();
    if (date)   r.date   = date;
    if (phone)  r.phone  = phone;
    out.push(r);
  });
  return out;
}

/** Which mapping fields are still needed before a reconcile makes sense. */
export function mappingProblems(mapping) {
  const problems = [];
  if (!mapping) return ["No columns mapped"];
  if (!mapping.amount) problems.push("Amount column is not mapped");
  if (!mapping.financialTransactionId && !mapping.externalId && !mapping.referenceId) {
    problems.push("Map at least one id column (financial transaction id, external id or reference id)");
  }
  return problems;
}

export const csvEscape = (v) => {
  if (v == null) return "";
  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Flatten a reconcile result into one CSV (with a `category` column) for the
 * "Download mismatches" button. Matched rows are excluded on purpose.
 */
export function buildMismatchCsv(result) {
  const cols = ["category", "disbursementId", "financialTransactionId", "externalId", "referenceId",
                "lusukuAmount", "mtnAmount", "lusukuStatus", "mtnStatus", "momoFinancialTxId", "phone", "date"];
  const lines = [cols.join(",")];
  if (!result) return lines.join("\n");
  const push = (category, r) => {
    lines.push(cols.map((c) => csvEscape(c === "category" ? category : r[c])).join(","));
  };
  (result.amountMismatch || []).forEach((r) => push("AMOUNT_MISMATCH", {
    ...r, lusukuAmount: r.lusukuAmount, mtnAmount: r.mtnAmount,
  }));
  (result.statusMismatch || []).forEach((r) => push("STATUS_MISMATCH", {
    ...r, lusukuStatus: r.lusukuStatus ?? r.status, mtnStatus: r.mtnStatus,
    lusukuAmount: r.lusukuAmount ?? r.amount,
  }));
  (result.missingInLusuku || []).forEach((r) => push("MISSING_IN_LUSUKU", {
    ...r, mtnAmount: r.amount, mtnStatus: r.status,
  }));
  (result.missingAtMtn || []).forEach((r) => push("MISSING_AT_MTN", {
    ...r, lusukuAmount: r.amount, lusukuStatus: r.status,
  }));
  return lines.join("\n");
}
